import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import { Sheet, SheetContent } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { SearchableCombobox } from "../../components/SearchableCombobox";
import { DrawingDiscPDList, DPsubPDList, DPsubPD2List } from "../../utils/publicVar";
import { useSwalOpen } from "../../hooks/use-swal-open";

const AdrawingtableDrawer = ({
  title,
  open,
  rows,
  rowIndex,
  editRow,
  onClose,
  onChangeRow,
  onMovePrev,
  onMoveNext,
  onSave,
  handleShowModal,
  moduleDialogOpen = false,
  fields, // SHEET_FIELDS
  areaList = [],
  vendorList = [],
}) => {
  const swalOpen = useSwalOpen();

  React.useEffect(() => {
    if (swalOpen) {
      document.querySelector(".swal2-confirm")?.focus();
    }
  }, [swalOpen]);

  if (!editRow) return null;

  const handleChange = (key, newValue) => {
    if (key === "DPsubPD") {
      onChangeRow({ ...editRow, DPsubPD: newValue, DPsubPD2: "" });
    } else {
      onChangeRow({ ...editRow, [key]: newValue });
    }
  };

  const dpsubPD2Options =
    editRow["DPsubPD"] === "배관"
      ? ["모듈", "비모듈"]
      : editRow["DPsubPD"] === "HVAC"
        ? ["GAL'V", "GAL'V 외"]
        : ["-"];

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => !v && onClose()}
      modal={!moduleDialogOpen && !swalOpen}
    >
      <SheetContent
        side="right"
        className="w-3/4 p-0 gap-0 sm:max-w-[440px] flex flex-col h-full [&_label]:text-xs [&_input]:!text-xs"
        onInteractOutside={(e) => {
          if (moduleDialogOpen || swalOpen) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (moduleDialogOpen || swalOpen) e.preventDefault();
        }}
      >
        {/* ===== Header (고정) ===== */}
        <div className="flex flex-shrink-0 items-center justify-center gap-2 border-b border-[#e0e0e0] px-4 py-3">
          <Button variant="ghost" size="icon" onClick={onMovePrev} disabled={rowIndex === 0}>
            <ChevronLeft />
          </Button>

          <div className="font-semibold">
            {title} · {rowIndex + 1} / {rows.length}
          </div>

          <Button variant="ghost" size="icon" onClick={onMoveNext} disabled={rowIndex === rows.length - 1}>
            <ChevronRight />
          </Button>
        </div>

        {/* ===== Scroll Area ===== */}
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-2">
          {/* 레코드번호 */}
          <div className="space-y-1.5">
            <Label>레코드 번호</Label>
            <Input type="text" value={editRow["record_no"]} disabled />
          </div>
          {/* 공종* */}
          <SearchableCombobox
            label="공종"
            value={editRow["DrawingDiscPD"]}
            onChange={(newValue) => handleChange("DrawingDiscPD", newValue)}
            options={DrawingDiscPDList.map((v) => ({ value: v, label: v }))}
            placeholder="공종 선택"
          />
          {/* 상세공종* */}
          <SearchableCombobox
            label="상세공종"
            value={editRow["DPsubPD"]}
            onChange={(newValue) => handleChange("DPsubPD", newValue)}
            options={DPsubPDList.map((v) => ({ value: v, label: v }))}
            placeholder="상세공종 선택"
          />
          {/* 구분* */}
          <SearchableCombobox
            label="구분"
            value={editRow["DPsubPD2"]}
            onChange={(newValue) => handleChange("DPsubPD2", newValue)}
            options={dpsubPD2Options.map((v) => ({ value: v, label: v }))}
            placeholder="구분 선택"
          />
          {/* Area* */}
          <SearchableCombobox
            label="Area"
            value={editRow["DPAreaBPK"]}
            onChange={(newValue) => handleChange("DPAreaBPK", newValue)}
            options={areaList.map((v) => ({ value: v, label: v }))}
            placeholder="Area 선택"
          />
          {/* 층* */}
          <div className="space-y-1.5">
            <Label>층</Label>
            <Input
              type="text"
              value={editRow["DPFloor"] ?? ""}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DPFloor: e.target.value,
                })
              }
            />
          </div>
          {/* 도면(문서) No* */}
          <div className="space-y-1.5">
            <Label>도면(문서) No</Label>
            <Input
              type="text"
              value={editRow["DrawingNo"] ?? ""}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DrawingNo: e.target.value,
                })
              }
            />
          </div>
          {/* 도면명* */}
          <div className="space-y-1.5">
            <Label>도면명</Label>
            <Input
              type="text"
              value={editRow["DrawingName"] ?? ""}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DrawingName: e.target.value,
                })
              }
            />
          </div>
          {/* 리비전* */}
          <div className="space-y-1.5">
            <Label>리비전</Label>
            <Input
              type="text"
              value={editRow["DPRevNo"] ?? ""}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  DPRevNo: e.target.value,
                })
              }
            />
          </div>
          {/* 협력업체* */}
          <SearchableCombobox
            label="협력업체"
            value={editRow["VendorsDPK"]}
            onChange={(newValue) => handleChange("VendorsDPK", newValue)}
            options={vendorList.map((v) => ({ value: v, label: v }))}
            placeholder="협력업체 선택"
          />
          {/* Module No */}
          <div className="space-y-1.5">
            <Label>모듈넘버</Label>
            <Input
              type="button"
              className="bg-[#f9fcff] text-left"
              onClick={() => handleShowModal(editRow.id)}
              value={editRow["MDModuleNoDPK"]}
            />
          </div>
          {/* Module 입고일 */}
          <div className="space-y-1.5">
            <Label>모듈 입고일</Label>
            <Input type="date" value={dayjs(editRow["MDSitePlanDO"]).format("YYYY-MM-DD")} disabled />
          </div>
          {/* 기준 날짜 */}
          <div className="space-y-1.5">
            <Label>기준 날짜</Label>
            <Input
              type="date"
              value={dayjs(editRow["WStart"]).format("YYYY-MM-DD")}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  WStart: new Date(e.target.value),
                })
              }
            />
          </div>
          {/* 접수 계획일 */}
          <div className="space-y-1.5">
            <Label>접수 계획일</Label>
            <Input
              type="date"
              value={dayjs(editRow["ReceiptPlanDate"]).format("YYYY-MM-DD")}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  ReceiptPlanDate: new Date(e.target.value),
                })
              }
            />
          </div>
          {/* 완료 계획일 */}
          <div className="space-y-1.5">
            <Label>완료 계획일</Label>
            <Input
              type="date"
              value={dayjs(editRow["CompletionPlanDate"]).format("YYYY-MM-DD")}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  CompletionPlanDate: new Date(e.target.value),
                })
              }
            />
          </div>
          {/* 상신 계획일 */}
          <div className="space-y-1.5">
            <Label>상신 계획일</Label>
            <Input
              type="date"
              value={dayjs(editRow["SubmissionPlanDate"]).format("YYYY-MM-DD")}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  SubmissionPlanDate: new Date(e.target.value),
                })
              }
            />
          </div>
          {/* 승인 계획일 */}
          <div className="space-y-1.5">
            <Label>승인 계획일</Label>
            <Input
              type="date"
              value={dayjs(editRow["ApprovalPlanDate"]).format("YYYY-MM-DD")}
              className="bg-[#f9fcff]"
              onChange={(e) =>
                onChangeRow({
                  ...editRow,
                  ApprovalPlanDate: new Date(e.target.value),
                })
              }
            />
          </div>
        </div>

        {/* ===== Footer (고정) ===== */}
        <div className="flex flex-shrink-0 gap-2 border-t border-[#e0e0e0] px-4 py-3">
          <Button className="flex-1" onClick={onSave}>
            저장
          </Button>
          <Button variant="outline" className="flex-1" onClick={onClose}>
            닫기
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AdrawingtableDrawer;
